import { useEffect, useRef, useState } from "react";
import { Button, ProgressBar, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../app/hooks";
import { selectCalcOperation, selectCalcProgress, selectCalcProgressSteps, selectCalcStartedAt } from "../features/ui/uiSlice";
import type { CalcProgressStep } from "../features/ui/uiSlice";
import { abortCurrentCalc } from "../features/projects/projectSlice";

interface SXCalcProgressOverlayProps {
  /**
   * "app" covers the window (analyse / code check, started from the toolbar); "panel" covers the
   * nearest positioned ancestor (the design panel, which locks only itself).
   */
  variant?: "app" | "panel";
  /**
   * Show the overlay even though no request is in flight yet. The design panel needs this for its
   * local prep step (fetching missing material/section data before the thunk is dispatched).
   */
  show?: boolean;
  /** Title to show until the first progress line arrives. */
  fallbackLabelKey?: string;
  fallbackLabel?: string;
}

/** How often the elapsed-time display refreshes between server progress lines. */
const CLOCK_MS = 500;

/**
 * The engine's own count, when the run got one. Numbers only, so there is nothing to translate —
 * which is also why it is a bare "7 / 24" rather than a sentence naming what is being counted
 * (that varies by segment, and the segment is already what the phase title reflects).
 */
const stepText = (progress: { step?: number; totalSteps?: number; stepLabelKey?: string }): string => {
  // A step with a label of its own says its number in its own words ("Cracking iteration 3"), and
  // a bare "· 3" next to that would only repeat it without saying what it counts.
  if (progress.step === undefined || progress.stepLabelKey) return "";
  return progress.totalSteps ? ` · ${progress.step} / ${progress.totalSteps}` : ` · ${progress.step}`;
};

/**
 * How one step reads: the name of the thing being worked on when the engine gave one, else the
 * name of the phase. A count, when there is one, follows it.
 *
 * The name is project data and untranslatable, so the wording around it is supplied here — which
 * is also why a step with no name falls back to the phase label rather than to English.
 */
const stepLabel = (
  step: CalcProgressStep,
  t: ReturnType<typeof useTranslation>["t"],
  fallbackLabel: string,
): string => {
  // On a design run the whole code check is repeated per candidate, so which repetition this is
  // has to be said outright - otherwise the same combinations cycle past indistinguishably.
  const pass =
    step.designPass !== undefined
      ? t("progress_design_pass_.label", { defaultValue: "Design pass {{pass}}", pass: step.designPass }) + " · "
      : "";

  // A column check runs once per bending plane and merges the two, so every load combination is
  // reported twice. Saying which direction is what stops that reading as a restart.
  const direction = step.direction
    ? t("progress_direction_.label", { defaultValue: "Direction {{plane}}", plane: step.direction }) + " · "
    : "";
  const prefix = `${pass}${direction}`;

  // A heading: it names the load combination the steps under it belong to, and carries its own
  // count, so stepText is not appended on top of it.
  if (step.contextLabelKey && step.detail) {
    const counted = step.step !== undefined && step.totalSteps !== undefined;
    return prefix + (counted
      ? t(`${step.contextLabelKey}counted_.label`, {
          defaultValue: "Calculating load combination ({{step}}/{{total}}) : {{name}}",
          step: step.step,
          total: step.totalSteps,
          name: step.detail,
        })
      : t(`${step.contextLabelKey}.label`, {
          defaultValue: "Calculating load combination : {{name}}",
          name: step.detail,
        }));
  }

  // The engine's own activity - a sub-analysis, a cracking iteration, waiting for FEM-Design - which
  // is wording rather than a name out of the project, so it comes from the locale files.
  const named = step.stepLabelKey
    ? t(`${step.stepLabelKey}.label`, {
        defaultValue: fallbackLabel,
        step: step.step ?? 0,
        total: step.totalSteps ?? 0,
        name: step.detail ?? "",
      })
    : step.detail
      ? t("progress_detail_.label", { defaultValue: "Working on {{name}}", name: step.detail })
      : t(`${step.labelKey}.label`, { defaultValue: fallbackLabel });
  // The engine names a load combination once and then reports its phases without repeating it,
  // so the context is what tells "Shear capacity" apart from the same step in the next
  // combination. Both halves are project data, so the separator is all this adds.
  const inContext = step.context ? `${step.context} : ${named}` : named;
  return `${prefix}${inContext}${stepText(step)}`;
};

/**
 * Progress feedback for the long calculation routes.
 *
 * Fed by UISlice, which is fed by the NDJSON progress lines the server streams once a call runs
 * past ~800 ms (see server/calculation/progress.ts). Nothing arrives for short calls, so the
 * overlay shows just the spinner and title — which is exactly what it used to do always.
 *
 * The phase title is real information: it says which stage the server is actually in. The bar is
 * an interpolation against expected durations for a run whose calculation engine does not report
 * its own progress — so it deliberately stops at 90% and only the result completes the run. When
 * the engine does report (see setUpstream on the server), the bar is computed from its counts and
 * the step readout below appears; both are read from the same fields either way.
 */
export const SXCalcProgressOverlay = ({
  variant = "app",
  show = false,
  fallbackLabelKey = "calculating_",
  fallbackLabel = "Calculating…",
}: SXCalcProgressOverlayProps) => {
  const { t } = useTranslation();
  const operation = useAppSelector(selectCalcOperation);
  const progress = useAppSelector(selectCalcProgress);
  const steps = useAppSelector(selectCalcProgressSteps);
  const startedAt = useAppSelector(selectCalcStartedAt);

  const visible = show || operation !== null;

  // Local clock so the elapsed seconds keep moving between progress lines (and while a fast call
  // has not produced any at all).
  const listRef = useRef<HTMLOListElement | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!visible) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), CLOCK_MS);
    return () => clearInterval(id);
  }, [visible]);

  // Keep the step in progress in view as the list grows past its height.
  useEffect(() => {
    const list = listRef.current;
    if (list) {
      list.scrollTop = list.scrollHeight;
    }
    // Keyed on the newest step, not the count: uiSlice caps the list, so past the cap the length
    // stops changing and this would never fire again on precisely the long runs that need it.
  }, [steps.length, steps[steps.length - 1]?.key]);

  if (!visible) return null;

  const elapsedMs = startedAt !== null ? now - startedAt : (progress?.elapsedMs ?? 0);
  const elapsedSeconds = Math.max(0, Math.round(elapsedMs / 1000));
  const title = progress
    ? t(`${progress.labelKey}.label`, fallbackLabel)
    : t(`${fallbackLabelKey}.label`, fallbackLabel);

  return (
    <div className={`sx-calc-progress-overlay sx-calc-progress-overlay--${variant}`} role="status" aria-live="polite">
      <div className="sx-calc-progress-overlay__card">
        <Spinner animation="border" role="status" aria-hidden="true" />
        <div className="sx-calc-progress-overlay__title">{title}</div>

        {progress && (
          // Once the bar reaches its 90% cap the estimate has been used up and there is nothing
          // left to measure against, so it switches to a moving stripe: still working, but no
          // longer claiming to know how far along it is. The explicit "longer than usual" note
          // below comes later, once the run is genuinely out of the ordinary.
          <ProgressBar
            now={progress.pct}
            animated={progress.pct >= 90}
            striped={progress.pct >= 90}
            className="sx-calc-progress-overlay__bar"
            aria-label={title}
          />
        )}

        {/* Numbers only — nothing to translate. */}
        <div className="sx-calc-progress-overlay__text">
          {progress ? `${progress.pct}% · ${elapsedSeconds} s${stepText(progress)}` : `${elapsedSeconds} s`}
        </div>

        {/* The steps this run has been through, the last one being the one in progress. Each
            name comes from the project, so only the wording around it is translated. */}
        {steps.length > 0 && (
          <ol className="sx-calc-progress-overlay__steps" ref={listRef}>
            {steps.map((s, i) => {
              const current = i === steps.length - 1;
              return (
                <li
                  key={`${s.key}-${i}`}
                  className={
                    current
                      ? "sx-calc-progress-overlay__step sx-calc-progress-overlay__step--current"
                      : "sx-calc-progress-overlay__step"
                  }
                >
                  <span className="sx-calc-progress-overlay__step-mark" aria-hidden="true">
                    {current ? "›" : "✓"}
                  </span>
                  <span className="sx-calc-progress-overlay__step-text">{stepLabel(s, t, fallbackLabel)}</span>
                </li>
              );
            })}
          </ol>
        )}

        {progress?.slow && (
          <div className="sx-calc-progress-overlay__slow">
            {t("progress_slow_.label", "This is taking longer than usual — still working…")}
          </div>
        )}

        {operation !== null && (
          <Button
            size="sm"
            variant="outline-secondary"
            className="sx-calc-progress-overlay__cancel"
            onClick={() => abortCurrentCalc()}
          >
            {t("cancel_.label", "Cancel")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default SXCalcProgressOverlay;
